import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { EVENTS_DATA, type EventData } from "../lib/events-data";
import { api } from "@repo/api-client";
import type { Event } from "@repo/shared-types";

function apiEventToEventData(e: Event): EventData {
  const dateRange = e.date_end
    ? `${new Date(`${e.date_start}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${new Date(`${e.date_end}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`
    : e.date_start;
  return {
    slug: e.slug,
    title: e.title,
    subtitle: e.subtitle,
    dateRange,
    startDate: e.date_start,
    endDate: e.date_end ?? "",
    location: e.location_name,
    locationDetail: e.location_address,
    type: e.type,
    status: e.status,
    heroEmoji: e.type === "retreat" ? "🌿" : e.type === "satsang" ? "📿" : e.type === "online" ? "💻" : "🔧",
    excerpt: e.description ?? "",
    tags: e.tags ?? [],
    minPrice: null,
  };
}

function getDateBadge(date: string, recurringLabel?: string) {
  if (recurringLabel) {
    return { month: "Every", day: recurringLabel };
  }

  const parsed = new Date(`${date}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return { month: "Soon", day: "New" };
  }

  return {
    month: parsed.toLocaleDateString("en-US", { month: "short" }).toUpperCase(),
    day: parsed.toLocaleDateString("en-US", { day: "2-digit" }),
  };
}

const STATUS_LABELS: Record<string, string> = {
  open: "Registration Open",
  upcoming: "Coming Soon",
  "sold-out": "Sold Out",
  past: "Past Event",
};

const STATUS_CLASSES: Record<string, string> = {
  open: "badge badge--success",
  upcoming: "badge badge--pending",
  "sold-out": "badge badge--error",
  past: "badge badge--muted",
};

const TYPE_LABELS: Record<string, string> = {
  retreat: "🌿 Retreat",
  satsang: "📿 Satsang",
  workshop: "🔧 Workshop",
  online: "💻 Online",
};

export function EventsListPage() {
  const [allEvents, setAllEvents] = useState<EventData[]>(EVENTS_DATA);

  useEffect(() => {
    api.listEvents().then((res) => {
      if (res.events.length > 0) setAllEvents(res.events.map(apiEventToEventData));
    }).catch(() => {/* use static fallback */});
  }, []);

  const upcoming = allEvents.filter((e) => e.status !== "past");
  const past = allEvents.filter((e) => e.status === "past");

  return (
    <>
      {/* Hero */}
      <section className="page-hero page-hero--brand">
        <div className="container">
          <span className="eyebrow">Events &amp; Gatherings</span>
          <h1 className="display-1 page-hero__title-spaced">Join a Sacred Event</h1>
          <p className="body-xl text-muted" style={{ maxWidth: "580px", margin: "0 auto 2rem" }}>
            Retreats, satsangs, and intensives designed for genuine transformation.
            In-person and online gatherings for all levels.
          </p>
          <div className="hero-actions" style={{ justifyContent: "center" }}>
            <a href="#upcoming" className="btn btn--brand">View Events</a>
            <Link to="/volunteer" className="btn btn--outline">Volunteer at Events</Link>
          </div>
        </div>
      </section>

      {/* Upcoming events */}
      <section id="upcoming" className="section">
        <div className="container">
          <div className="section-header">
            <span className="eyebrow">Upcoming</span>
            <h2 className="display-3">Events &amp; Retreats</h2>
          </div>
          <div className="events-grid" style={{ marginTop: "2.5rem" }}>
            {upcoming.map((event) => (
              (() => {
                const dateBadge = getDateBadge(
                  event.startDate,
                  event.dateRange.toLowerCase().startsWith("every") ? "SUN" : undefined,
                );

                return (
              <Link
                key={event.slug}
                to={`/events/${event.slug}`}
                className={`event-card event-card--${event.type}`}
              >
                <div className="event-card__hero">
                  <div className="event-card__hero-top">
                    <div className="event-card__date-badge" aria-label={`Starts ${event.dateRange}`}>
                      <span className="event-card__date-month">{dateBadge.month}</span>
                      <span className="event-card__date-day">{dateBadge.day}</span>
                    </div>
                    <span className={`${STATUS_CLASSES[event.status]} event-card__status`}>
                      {STATUS_LABELS[event.status]}
                    </span>
                  </div>
                  <div className="event-card__hero-bottom">
                    <span className="event-card__emoji" aria-hidden="true">
                      {event.heroEmoji}
                    </span>
                    <span className="event-card__hero-ring" aria-hidden="true" />
                  </div>
                </div>
                <div className="event-card__body">
                  <div className="event-card__eyebrow-row">
                    <div className="event-card__type">{TYPE_LABELS[event.type]}</div>
                    <div className="event-card__price-pill">
                      {event.minPrice === 0 ? "Free / PWYC" : event.minPrice !== null ? `From $${event.minPrice}` : "Free"}
                    </div>
                  </div>
                  <h3 className="event-card__title">{event.title}</h3>
                  <p className="event-card__subtitle">{event.subtitle}</p>
                  <div className="event-card__meta">
                    <span className="event-card__meta-item event-card__meta-item--calendar">
                      <strong>When</strong>
                      <span>{event.dateRange}</span>
                    </span>
                    <span className="event-card__meta-item event-card__meta-item--location">
                      <strong>Where</strong>
                      <span>{event.location}</span>
                    </span>
                  </div>
                  <p className="event-card__excerpt">{event.excerpt}</p>
                  <div className="event-card__tags" aria-label="Event focus areas">
                    {event.tags.slice(0, 3).map((tag) => (
                      <span key={tag} className="event-card__tag">{tag}</span>
                    ))}
                  </div>
                  <div className="event-card__footer">
                    <div className="event-card__price-note">
                      {event.minPrice === 0 ? (
                        <span className="event-price--free">Accessible pricing</span>
                      ) : event.minPrice !== null ? (
                        <span>Reserve your place</span>
                      ) : (
                        <span>Open access</span>
                      )}
                    </div>
                    <span className="event-card__cta">
                      Learn More
                      <span aria-hidden="true">→</span>
                    </span>
                  </div>
                </div>
              </Link>
                );
              })()
            ))}
          </div>
        </div>
      </section>

      {/* Tags strip */}
      <section className="section section--surface-alt">
        <div className="container" style={{ textAlign: "center" }}>
          <span className="eyebrow">Filter by Focus</span>
          <div className="events-tags-strip" style={{ marginTop: "1rem" }}>
            {["All", "Meditation", "Retreat", "Online", "Satsang", "Yoga", "Astral", "Free"].map((tag) => (
              <span key={tag} className="events-tag-chip">{tag}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Past events */}
      {past.length > 0 && (
        <section className="section">
          <div className="container">
            <div className="section-header">
              <span className="eyebrow">Archive</span>
              <h2 className="display-3">Past Events</h2>
            </div>
            <div className="events-grid" style={{ marginTop: "2rem", opacity: 0.7 }}>
              {past.map((event) => (
                <div key={event.slug} className="event-card event-card--past">
                  <div className="event-card__body">
                    <div className="event-card__type">{TYPE_LABELS[event.type]}</div>
                    <h3 className="event-card__title">{event.title}</h3>
                    <div className="event-card__meta">
                      <span className="event-card__meta-item">📅 {event.dateRange}</span>
                      <span className="event-card__meta-item">📍 {event.location}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="section section--cta">
        <div className="container container--narrow" style={{ textAlign: "center" }}>
          <h2 className="display-3">Want to help at an event?</h2>
          <p className="body-lg text-muted" style={{ margin: "1rem 0 2rem" }}>
            Our Sena volunteers are the heart of every gathering. Join the team.
          </p>
          <Link to="/volunteer" className="btn btn--brand">Become a Volunteer</Link>
        </div>
      </section>
    </>
  );
}
